import { cloneDeep } from "lodash";
import { getDefaultLanguageCode, isSameLanguage } from "@/lib/language-code-utils";
import { genSceneId } from "@/lib/clip-utils";

export class StreamScene {
  stream: AutomationStream;

  project: AutomationProjectType;

  sceneIdx: number | null;

  template: AutomationProjectType;

  templateSceneIdx: number | null;

  scene: Scene | null;

  ttsList: Tts[];

  isCustomAvatar: boolean;

  constructor(
    stream: AutomationStream,
    project: AutomationProjectType,
    template: AutomationProjectType,
    ttsList: Tts[],
    isCustomAvatar:boolean
  ) {
    this.stream = stream;
    this.project = project;
    this.sceneIdx = stream.sceneIdx ?? null;
    this.template = template;
    this.templateSceneIdx = stream.templateSceneIdx ?? null;
    this.scene = null;
    this.ttsList = ttsList;
    this.isCustomAvatar = isCustomAvatar;
  }


  initScene = () => {
    if (
      this.sceneIdx === null ||
      this.templateSceneIdx === null
    ) {
      this.scene = null;
      return this;
    }

    if (this.project.scenes[this.sceneIdx]) {
      this.scene = { ...this.project.scenes[this.sceneIdx] };

      return this;
    }

    const modelClip = this.template.scenes[this.templateSceneIdx].clips.find((clip) => clip.type === "aiModel");

    if (!modelClip) {
      this.scene = null;
      return this;
    }

    const { isTTS, tts } = (() => {
      const avatarType = modelClip.avatarType;

      if (avatarType === "original" || !avatarType) {
        return { isTTS: false, tts: null };
      }

      const pickedTts = (() => {
        const gender = modelClip.model.gender;
        const normal = this.getTTS({ language: "en", gender });

        if (normal) {
          return normal;
        }

        return this.ttsList.filter((voice) => voice.gender === gender)[0];
      })();

      if (!modelClip.model.ttsId) {
        return { isTTS: true, tts: pickedTts };
      }

      const modelTts = this.template.scenes[this.templateSceneIdx].scripts[0].tts;

      if (modelTts) {
        return { isTTS: true, tts: modelTts };
      }

      return { isTTS: true, tts: pickedTts };
    })();

    this.scene = {
      ...this.template.scenes[this.templateSceneIdx],
      id: genSceneId(this.project.scenes),
      clips: this.template.scenes[this.templateSceneIdx].clips
        .filter((clip) => {
          if (clip.type === "aiModel") {
            return true;
          }
          if (clip.type === "captions") {
            return true;
          }
          if (clip.tag === "logo") {
            return true;
          }
          if (!Object.hasOwn(clip, "tag") || clip.tag === "") {
            return true;
          }
          return false;
        })
        .map((clip) => {
          if (clip.type === "aiModel") {
            return {
              ...clip,
              script: {
                ...clip.script,
                org: `<p></p>`,
              },
            };
          }
          if (clip.type === "captions") {
            return {
              ...clip,
              text: "",
              captions: [],
            };
          }
          return { ...clip };
        }),
      scripts: [{
        org: `<p></p>`,
        clipId: modelClip.id,
        modelId: modelClip.model.ai_name,
        clothId: modelClip.model.emotion,
        modelSupportLanguage: modelClip.model.language,
        gender: modelClip.model.gender,
        isTTS,
        tts,
      }],
    };

    return this;
  }

  getTTS = (props: {
    language: string;
    gender: string;
  }) => {
    return this.ttsList
      .filter((voice) => voice.gender === props.gender)
      .sort((a, b) => {
        if (a.type !== b.type) {
          if (a.type === "google") {
            return -1;
          } else {
            return 1;
          }
        }
        return 0;
      })
      .find((voice) => {
        const code = getDefaultLanguageCode(props.language, true);
        if (voice.language.toLowerCase().startsWith(code)) {
          return voice.language.toLowerCase().startsWith(code);
        }
        return voice.language.toLowerCase().startsWith(code.split("-")[0]);
      }) || null;
  }

  setClips = () => {
    if (!this.stream.clips) {
      return this;
    }
    if (!this.scene) {
      console.error("streamScene.setClips(): scene is null");
      return this;
    }
    if (
      this.sceneIdx === null ||
      this.templateSceneIdx === null
    ) {
      return this;
    }
    const scene = this.scene;
    const templateSceneIdx = this.templateSceneIdx;

    this.stream.clips.forEach((streamClip) => {
      const templateClip = this.template.scenes[templateSceneIdx].clips.find(
        (clip) => clip.id === streamClip.id
      );
      const projectClip = this.scene?.clips.find(
        (clip) => clip.id === streamClip.id
      );
      const streamClipProps = { ...streamClip };

      if (streamClipProps?._replaceId) {
        streamClipProps.id = streamClipProps._replaceId;
        // streamClipProps.type = streamClipProps._replaceId.split("-")[0];
        delete streamClipProps._replaceId;
      }

      // clips
      if (projectClip) {
        scene.clips = scene.clips.map((clip) => {
          if (clip.id === streamClip.id) {
            return {
              ...projectClip,
              ...streamClipProps,
            };
          }
          return clip;
        });
      } else if (templateClip) {
        scene.clips = [
          ...scene.clips,
          {
            ...templateClip,
            ...streamClipProps,
          },
        ];
      }
    });
    return this;
  }

  setScript = () => {
    if (!this.stream.script && !this.stream.finalScript) {
      return this;
    }
    if (!this.scene) {
      console.error("streamScene.setScript(): scene is null");
      return this;
    }
    const streamScript = this.stream.script || this.stream.finalScript;
    const scene = this.scene;
    // clip
    // scene.clips = scene.clips.map((clip) => {
    //   if (clip.type === "aiModel") {
    //     return {
    //       ...clip,
    //       script: {
    //         ...clip.script,
    //         org: `<p>${streamScript}</p>`,
    //       },
    //     };
    //   }
    //   return clip;
    // });

    // scripts
    scene.scripts = scene.scripts
      .filter((script, index) => index === 0)
      .map((script) => {
        return {
          ...script,
          org: `<p>${streamScript}</p>`,
        };
      });

    return this;
  }

  setLanguage = () => {
    if (!this.stream.finalScriptLang) {
      return this;
    }
    if (!this.scene) {
      console.error("streamScene.setLanguage(): scene is null");
      return this;
    }
    const finalScriptLang = this.stream.finalScriptLang.split("-")[0];
    const scene = this.scene;
    // clip

    // scene.clips = scene.clips.map((clip) => {
    //   if (clip.type === "aiModel") {
    //     if (clip.model.language !== finalScriptLang) {
    //       const tts = this.getTTS({
    //         language: finalScriptLang,
    //         gender: clip.model.gender,
    //       });

    //       if (tts) {
    //         return {
    //           ...clip,
    //           script: {
    //             ...clip.script,
    //             detectedLanguage: finalScriptLang,
    //             detectionMarked: true,
    //             tts: tts,
    //             isTTS: true,
    //           },
    //         } as Clip;
    //       }
    //     } else {
    //       return {
    //         ...clip,
    //         script: {
    //           ...clip.script,
    //           detectedLanguage: finalScriptLang,
    //           detectionMarked: true,
    //           tts: null,
    //           isTTS: false,
    //         }
    //       } as Clip;
    //     }
    //   }
    //   return clip;
    // });

    // scripts
    scene.scripts = scene.scripts.map((script) => {
      const s = cloneDeep(script);

      s.detectedLanguage = finalScriptLang;
      s.detectionMarked = true;

      if (s.isTTS && !!s.tts && s.tts.language === s.detectedLanguage) {
        return s;
      }

      if (s.isTTS && !!s.tts && s.tts.language === "multi") {
        return s;
      }

      if (!s.isTTS && (s.modelSupportLanguage === s.detectedLanguage)) {
        return s;
      }

      const modelClip = scene.clips.find((clip) => clip.id === s.clipId);

      if (!modelClip) {
        return s;
      }
      
      const avatarType = modelClip.avatarType;
      const gender = modelClip.model.gender;

      if (avatarType === "original" || avatarType === undefined) {
        s.isMultiLanguage = true;
        s.isTTS = false;
        s.tts = null;

        return s;
      }
      
      if (!s.isTTS) {
        const sameLanguageTts = this.getTTS({ language: s.detectedLanguage, gender });

        if (sameLanguageTts) {
          s.isTTS = true;
          s.tts = sameLanguageTts;

          return s;
        }
      }

      const fallbackTts = this.ttsList.filter((voice) => voice.gender === gender)[0];

      s.isMultiLanguage = true;
      s.isTTS = true;
      s.tts = fallbackTts;

      return s;
    });

    return this;
  }

  setBackground = () => {
    if (!this.stream.background) {
      return this;
    }
    if (!this.scene) {
      console.error("streamScene.setBackground(): scene is null");
      return this;
    }
    this.scene.background = {
      ...this.scene.background,
      source_type: "image",
      source_url: this.stream.background.source_url,
    };
    return this;
  }

  setFinal = () => {
    if (!this.stream.finalScript) {
      return this;
    }
    if (!this.scene) {
      console.error("streamScene.setFinal(): scene is null");
      return this;
    }
    this.scene.isFinish = true;
    return this;
  }

  getScene = () => {
    this
      .initScene()
      .setClips()
      .setScript()
      .setLanguage()
      .setBackground()
      .setFinal();

    return this.scene;
  }
}